import { AUTH_PAGE_PATHS } from "@/lib/constants/auth";
import { SENSITIVE_AUTH_QUERY_PARAMS } from "@/lib/auth/sensitive-query";
import type { CaptureResult } from "posthog-js";

/**
 * Query and fragment keys masked in every PostHog URL value. `token` is
 * included because invite / reset links carry a credential in it, and the
 * OAuth-style keys because an implicit-flow or code redirect would put a
 * bearer credential in the URL.
 */
export const POSTHOG_MASKED_QUERY_PARAMS: string[] = [
  ...SENSITIVE_AUTH_QUERY_PARAMS,
  "token",
  "access_token",
  "id_token",
  "refresh_token",
  "code",
];

const MASKED_PARAMS_LOWER = new Set(
  POSTHOG_MASKED_QUERY_PARAMS.map((name) => name.toLowerCase()),
);

const MASKED_VALUE = "<MASKED>";

/**
 * Keys whose string value is always treated as a URL, even when it is not
 * shaped like one. Other string values are scrubbed only when they look
 * like a URL (see `looksLikeUrl`), so new SDK properties such as
 * `$session_entry_url` or `$initial_person_info.u` are covered without
 * listing them here.
 */
const URL_KEY_RE = /(url|referrer|href|pathname)$/i;

/** Nesting depth walked inside a property value (`$initial_person_info`). */
const MAX_DEPTH = 4;

function isAuthPath(pathname: string): boolean {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const lower = trimmed.toLowerCase();
  return AUTH_PAGE_PATHS.some(
    (route) => lower === route || lower.startsWith(`${route}/`),
  );
}

function maskParamList(params: URLSearchParams): boolean {
  let changed = false;
  for (const name of new Set(params.keys())) {
    if (MASKED_PARAMS_LOWER.has(name.toLowerCase())) {
      params.set(name, MASKED_VALUE);
      changed = true;
    }
  }
  return changed;
}

/**
 * Mask sensitive keys in a fragment. Handles `#a=b&c=d` and a hash-router
 * style `#/route?a=b`.
 */
function maskHash(hash: string): string {
  if (hash.length <= 1 || !hash.includes("=")) return hash;
  const body = hash.slice(1);
  const queryStart = body.indexOf("?");
  const prefix = queryStart === -1 ? "" : body.slice(0, queryStart + 1);
  const query = queryStart === -1 ? body : body.slice(queryStart + 1);
  const params = new URLSearchParams(query);
  if (!maskParamList(params)) return hash;
  const next = params.toString();
  return next || prefix ? `#${prefix}${next}` : "";
}

export function maskSensitiveQueryParams(value: string): string {
  let url: URL;
  try {
    url = new URL(value, "http://placeholder.invalid");
  } catch {
    return value;
  }

  // `origin` does not include userinfo, so a rebuild drops `user:password`
  // only when something else already changed. Clear it first and treat
  // that as a change, or a crafted link is sent as `$current_url`.
  const hadUserinfo = url.username !== "" || url.password !== "";
  url.username = "";
  url.password = "";

  const queryChanged = maskParamList(url.searchParams);
  // Auth pages never use a fragment, so drop it there entirely.
  const hash = isAuthPath(url.pathname) ? "" : maskHash(url.hash);

  if (!queryChanged && !hadUserinfo && hash === url.hash) return value;

  const relative = `${url.pathname}${url.search}${hash}`;
  return /^https?:\/\//i.test(value) ? `${url.origin}${relative}` : relative;
}

function looksLikeUrl(value: string): boolean {
  if (/^https?:\/\//i.test(value)) return true;
  return value.startsWith("/") && /[?#]/.test(value);
}

function scrubValue(value: unknown, key: string, depth: number): unknown {
  if (typeof value === "string") {
    return URL_KEY_RE.test(key) || looksLikeUrl(value)
      ? maskSensitiveQueryParams(value)
      : value;
  }
  if (depth >= MAX_DEPTH || value === null || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = scrubValue(value[i], key, depth + 1);
    }
    return value;
  }
  scrubObject(value as Record<string, unknown>, depth + 1);
  return value;
}

function scrubObject(props: Record<string, unknown>, depth: number): void {
  for (const key of Object.keys(props)) {
    props[key] = scrubValue(props[key], key, depth);
  }
}

/**
 * Session replay keeps the page URL in rrweb meta events (`type: 4`,
 * `data.href`). Only those are scrubbed; walking the full DOM snapshot on
 * every batch would be costly and replay already masks inputs.
 */
function scrubSnapshot(props: Record<string, unknown>): void {
  const data = props.$snapshot_data;
  if (!Array.isArray(data)) return;
  for (const item of data) {
    const meta = item as { type?: unknown; data?: { href?: unknown } } | null;
    if (meta?.type === 4 && typeof meta.data?.href === "string") {
      meta.data.href = maskSensitiveQueryParams(meta.data.href);
    }
  }
}

/**
 * PostHog `before_send` hook: mask credentials and tokens in every URL
 * value of the event, its `$set` and its `$set_once` person properties,
 * including `$session_entry_url`, `$initial_person_info` and referrers.
 */
export function scrubSensitiveUrls(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event) return event;
  const props = event.properties as Record<string, unknown> | undefined;
  if (props) {
    if (event.event === "$snapshot") {
      scrubSnapshot(props);
      for (const key of Object.keys(props)) {
        if (key !== "$snapshot_data")
          props[key] = scrubValue(props[key], key, 0);
      }
    } else {
      scrubObject(props, 0);
    }
  }
  if (event.$set) scrubObject(event.$set as Record<string, unknown>, 0);
  if (event.$set_once) {
    scrubObject(event.$set_once as Record<string, unknown>, 0);
  }
  return event;
}
